import { Alert } from '@e3dc-react/shell/Elements/Alert'
import Actions from '@e3dc-react/shell/Elements/Dialog/ActionDialog/Actions'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, DialogContent, Stack } from '@mui/material'
import { TFunction } from 'i18next'
import React, { useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { IoInterfaces, IoInterfaceType, Storage } from 'src/Libs/flow/installation/forms'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { makeStyles } from 'tss-react/mui'
import { z } from 'zod'
import { useDialogActionsPositioned } from '../../../hooks'

interface AddIoInterfaceForm {
  name: string
  deviceType: string
}

// #region Main Component

interface AddIoInterfaceProps {
  availableIo: Record<IoInterfaceType, boolean>
  error: string | null
  closeDialog: () => void
  ioInterfaces: IoInterfaces
  storages: Storage[]
  addIoInterface: (name: string, deviceType: string) => void
}

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    padding: `${theme.spacing(4)} ${theme.spacing(0)}`,
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  wideInput: {
    flex: 1,
  },
  narrowInput: {
    flex: 0.25,
  },
  inputColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))
/**
 * Form to add an I/O interface to the installation. Thought of as content inside a dialog.
 */
export const AddIoInterface: React.FC<AddIoInterfaceProps> = ({ availableIo, error, closeDialog, ioInterfaces, storages, addIoInterface }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const availableIoInterfaceOptions: Options<string> = getIoInterfaceOptions(t, storages).filter(option => {
    if (option.value === 'htc320h') return availableIo.htc320h
    if (option.value === 'mypv') return availableIo.mypv
    // for storage options, check if any storage matches the name and if storages are available
    return availableIo.storage && storages.some(storage => storage.deviceId === option.value)
  })
  const firstAvailableOption = availableIoInterfaceOptions[0]?.value

  const methods = useForm<AddIoInterfaceForm>({
    defaultValues: {
      name: getIoDefaultName(firstAvailableOption),
      deviceType: firstAvailableOption,
    },
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { control, handleSubmit, formState, setValue } = methods

  const deviceType = useWatch({ control, name: 'deviceType' })

  useEffect(() => {
    setValue('name', getIoDefaultName(deviceType), { shouldDirty: true })
  }, [deviceType, setValue])

  const onSubmit: SubmitHandler<AddIoInterfaceForm> = data => {
    closeDialog()
    addIoInterface(data.name, deviceType)
  }
  const isSaveButtonEnabled = !error && formState.isValid

  return (
    <FormProvider {...methods}>
      <Box component={'form'} display={'contents'} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers className={classes.dialogContent}>
          {error ? (
            <Box>
              <Alert severity="warning" sx={{ marginBottom: 2 }} text={error} />
              <Stack gap={3}>
                {!!ioInterfaces.htc320h && (
                  <Box className={classes.inputRow}>
                    <ShellTextField
                      className={classes.wideInput}
                      label={t('common.name')}
                      value={getDeviceDefaultName(ioInterfaces.htc320h?.name || '', 'HTC320H I/O', ioInterfaces.htc320h?.deviceId || '', t)}
                      disabled
                    />
                    <ShellTextField
                      className={classes.narrowInput}
                      label={t('productEditor.pairingEditor.pairingID.htc')}
                      value={ioInterfaces.htc320h?.pairingId}
                      disabled
                    />
                  </Box>
                )}
                {!!ioInterfaces.storage && (
                  <Box className={classes.inputRow}>
                    <ShellTextField
                      className={classes.wideInput}
                      label={t('common.name')}
                      value={getDeviceDefaultName(ioInterfaces.storage?.name || '', 'Storage I/O', ioInterfaces.storage?.deviceId || '', t)}
                      disabled
                    />
                    <ShellTextField
                      sx={{ flex: 0.25 }}
                      label={t('installation.summaryTable.deviceId')}
                      value={storages.find(storage => ioInterfaces?.storage?.id === storage.id)?.pairingId}
                      disabled
                    />
                  </Box>
                )}
                {!!ioInterfaces.mypv && (
                  <Box className={classes.inputRow}>
                    <ShellTextField
                      className={classes.wideInput}
                      label={t('common.name')}
                      value={getDeviceDefaultName(ioInterfaces.mypv?.name || '', 'MyPV I/O', ioInterfaces.mypv?.deviceId || '', t)}
                      disabled
                    />
                    <ShellTextField
                      className={classes.narrowInput}
                      label={t('productEditor.pairingEditor.pairingID.mypv')}
                      value={ioInterfaces.mypv?.pairingId}
                      disabled
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          ) : (
            <Box className={classes.inputColumn}>
              <TextField name="name" label={t('installation.productEditor.deviceName')} fullWidth required />
              <Select name="deviceType" label={t('installation.productEditor.controllerDevice')} options={availableIoInterfaceOptions} fullWidth />
            </Box>
          )}
        </DialogContent>
        <Actions
          actions={useDialogActionsPositioned(
            t('installation.productEditor.edit.ioInterface'),
            handleSubmit(onSubmit),
            !isSaveButtonEnabled,
            closeDialog
          )}
        />
      </Box>
    </FormProvider>
  )
}
//#endregion

//#region Helpers
const validationSchema = z.object({
  name: z.string().nonempty(),
})

const getIoDefaultName = (type: string): string => {
  if (type === 'mypv') {
    return 'my-PV'
  }
  return 'SG-Ready'
}

const getIoInterfaceOptions = (t: TFunction, storages: Storage[]): Options<string> => {
  const ioInterfaceOptions: Options<string> = [
    { value: 'htc320h', label: t('installation.types.modbusRTU') }, // "HTC320H"
    { value: 'mypv', label: t('installation.types.modbusTCP') }, // "MyPV"
  ]

  return ioInterfaceOptions.concat(
    // "Storages"
    storages.map(storage => ({
      value: storage.deviceId,
      label: `${storage.name} (${storage.pairingId})`,
    }))
  )
}
//#endregion
